import type { LoaderFunctionArgs } from '~/types/route-args';
import { data as routerData } from 'react-router';
import {
    ListBuildHubProjectsForMatchDocument,
    type ListBuildHubProjectsForMatchQuery,
    type ListBuildHubProjectsForMatchQueryVariables,
} from 'generated/graphql';
import type { MatchableProject } from '~/modules/get-matched/utils/match-buildhub-project';

export const MATCH_PROJECTS_API_ROUTE = '/remix-api/get-matched/match-projects';

export type MatchProjectsResponse =
    | { success: true; projects: MatchableProject[] }
    | { success: false; projects: MatchableProject[] };

const PAGE_SIZE = 100;

/**
 * Returns the member's BuildHub projects in the slim shape the matcher needs.
 * Always responds with `projects: []` rather than throwing — keeps the modal
 * flow uninterrupted for guests, network errors, and the no-projects case.
 */
export const loader = async ({ context }: LoaderFunctionArgs) => {
    const { client } = context;

    try {
        const { data } = await client.query<
            ListBuildHubProjectsForMatchQuery,
            ListBuildHubProjectsForMatchQueryVariables
        >({
            query: ListBuildHubProjectsForMatchDocument,
            variables: { limit: PAGE_SIZE, offset: 0 },
            fetchPolicy: 'network-only',
        });

        const projects = (data?.buildHubs?.nodes ?? []).map(toMatchable);

        return routerData<MatchProjectsResponse>({
            success: true,
            projects,
        });
    } catch {
        return routerData<MatchProjectsResponse>({
            success: false,
            projects: [],
        });
    }
};

const toMatchable = (
    node: ListBuildHubProjectsForMatchQuery['buildHubs']['nodes'][number],
): MatchableProject => ({
    ID: Number(node.ID),
    Title: node.Title,
    ProjectScope: node.ProjectScope ?? null,
    Location: node.Location ?? null,
    LocationData: node.LocationData
        ? {
              FullAddress: node.LocationData.FullAddress ?? null,
              Suburb: node.LocationData.Suburb ?? null,
              City: node.LocationData.City ?? null,
              Region: node.LocationData.Region ?? null,
          }
        : null,
    Budget: node.Budget
        ? {
              Min: node.Budget.Min ?? null,
              Max: node.Budget.Max ?? null,
          }
        : null,
    StartDate: node.StartDate ?? null,
    EndDate: node.EndDate ?? null,
    MainDescription: node.MainDescription ?? null,
    LastActivity: node.LastActivity ?? null,
});
